import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { useProfile } from '@/features/auth/useProfile';
import { formatDate, formatMoney } from '@/lib/format';
import {
  COMMISSION_STATUSES,
  useCommissions,
  useSetCommissionStatus,
  type Commission,
  type CommissionStatus,
} from './useDeals';

const STATUS_STYLES: Record<CommissionStatus, string> = {
  expected: 'bg-slate-100 text-slate-700',
  due: 'bg-amber-100 text-amber-900',
  invoiced: 'bg-blue-100 text-blue-900',
  received: 'bg-indigo-100 text-indigo-900',
  paid: 'bg-green-100 text-green-900',
};

/** The next step in the lifecycle, or null at the end of it. Forward only, which
 *  is what set_commission_status() enforces — this just avoids offering a move
 *  the database would refuse. */
function nextStatus(current: CommissionStatus): CommissionStatus | null {
  const i = COMMISSION_STATUSES.indexOf(current);
  return i >= 0 && i < COMMISSION_STATUSES.length - 1
    ? (COMMISSION_STATUSES[i + 1] as CommissionStatus)
    : null;
}

/**
 * Commissions (#24).
 *
 * Finance moves the lifecycle; managers and the CEO read it; a rep sees their own
 * rows and no buttons. None of that is enforced here — `commissions` has no UPDATE
 * policy at all and set_commission_status() demands finance or admin, so a hidden
 * button is a courtesy rather than the control.
 */
export default function CommissionsPage() {
  const { t } = useTranslation();
  const { data: me } = useProfile();

  const [status, setStatus] = useState('');
  const [includeVoided, setIncludeVoided] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filters = { status, includeVoided };
  const { data, isPending, isError } = useCommissions(filters);
  const setCommissionStatus = useSetCommissionStatus();

  const canAdvance = !!me && ['finance', 'admin'].includes(me.role);
  const rows = data ?? [];

  const advance = (commission: Commission) => {
    const next = nextStatus(commission.status);
    if (!next) return;
    setError(null);
    setCommissionStatus
      .mutateAsync({ commissionId: commission.id, status: next })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : t('common.error'));
      });
  };

  // Totals are computed from the rows actually shown, so the figure always agrees
  // with the list under it.
  const totals = rows.reduce(
    (acc, r) => {
      acc.gross += Number(r.gross_commission);
      acc.company += Number(r.company_share);
      acc.agent += Number(r.agent_share);
      return acc;
    },
    { gross: 0, company: 0, agent: 0 },
  );

  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-xl font-bold text-slate-900">{t('nav.commissions')}</h1>

      {error ? <Alert tone="error">{error}</Alert> : null}
      {isError ? <Alert tone="error">{t('common.error')}</Alert> : null}

      <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-3 sm:grid-cols-3">
        <Select
          label={t('commissions.status')}
          placeholder={t('commissions.allStatuses')}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          options={COMMISSION_STATUSES.map((s) => ({
            value: s,
            label: t(`commissionStatuses.${s}` as 'commissionStatuses.expected'),
          }))}
        />
        <label className="flex items-end gap-2 pb-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="size-4 rounded border-slate-300"
            checked={includeVoided}
            onChange={(e) => setIncludeVoided(e.target.checked)}
          />
          {t('commissions.showVoided')}
        </label>
      </div>

      {isPending ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          {t('commissions.none')}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 rounded-xl border border-slate-200 bg-white p-4 text-center">
            {(
              [
                ['gross', totals.gross],
                ['company', totals.company],
                ['agent', totals.agent],
              ] as const
            ).map(([key, value]) => (
              <div key={key}>
                <div className="text-lg font-semibold text-slate-900" dir="ltr">
                  {formatMoney(value)}
                </div>
                <div className="text-xs text-slate-500">
                  {t(`commissions.total.${key}` as 'commissions.total.gross')}
                </div>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('commissions.client')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('commissions.agent')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('commissions.unitValue')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('commissions.gross')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('commissions.agentShare')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('commissions.status')}
                  </th>
                  <th scope="col" className="px-3 py-2" />
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => {
                  const next = nextStatus(row.status);
                  const voided = Boolean(row.voided_at);

                  return (
                    <tr key={row.id} className={voided ? 'bg-slate-50 opacity-70' : ''}>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900">
                          {row.deals?.leads?.full_name ?? '—'}
                        </div>
                        <div className="text-xs text-slate-500">
                          {row.deals?.units?.unit_code ?? '—'}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-700">{row.profiles?.full_name ?? '—'}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-700" dir="ltr">
                        {formatMoney(row.unit_value)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-900" dir="ltr">
                        {formatMoney(row.gross_commission)}
                        <div className="text-xs text-slate-500">{row.commission_percent}%</div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-700" dir="ltr">
                        {formatMoney(row.agent_share)}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            STATUS_STYLES[row.status]
                          }`}
                        >
                          {t(`commissionStatuses.${row.status}` as 'commissionStatuses.expected')}
                        </span>
                        {voided ? (
                          <div className="mt-1 text-xs text-red-700">
                            {t('commissions.voided')}
                            {row.voided_reason ? `: ${row.voided_reason}` : ''}
                          </div>
                        ) : row.paid_date ? (
                          <div className="mt-1 text-xs text-slate-500">
                            {formatDate(row.paid_date)}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-end">
                        {canAdvance && next && !voided ? (
                          <Button
                            variant="secondary"
                            loading={setCommissionStatus.isPending}
                            onClick={() => advance(row)}
                          >
                            {t('commissions.markAs', {
                              status: t(
                                `commissionStatuses.${next}` as 'commissionStatuses.expected',
                              ),
                            })}
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
